/**
 * DataManager - Handles all CMS data fetching, caching, and lookups
 * Single source of truth for all project data from Webflow API
 */
class DataManager {
    static CONFIG = {
        API_ENDPOINT: 'https://faasgis-eky.pages.dev/api/tokens', 
        CACHE_DURATION: 10 * 60 * 1000, // 10 minutes in milliseconds
        RETRY_ATTEMPTS: 3,
        RETRY_DELAY: 1000
    };
    constructor() {
        // ! Raw data storage - Read "Array Object" from API_ENDPOINT
        this.cmsData = null;
        this.typologies = null;
        this.statuses = null;
        this.projectTypologyCode = null;
        this.projectStatusCode = null;
        this.mapboxToken = null;
        // ! Lookup maps (for fast access)
        this.typologyColorMap = new Map(); // typologyId -> color
        this.typologyNameMap = new Map(); // typologyId -> name
        this.typologySlugToIdMap = new Map(); // typologySlug -> typologyId
        this.statusNameMap = new Map(); // statusId -> name
        this.statusSlugToIdMap = new Map(); // statusSlug -> statusId
        // ! GeoJSON storage
        this.allLocations = [];
        this.visibleLocations = {
            type: "FeatureCollection",
            features: []
        };
        // ! Cache management
        this.lastFetchTime = null;
        this.isFetching = false;
    }
    /**
     * ! Fetch data from API with caching and retry logic
     * @returns {Promise<Object>} Raw API response data
     */
    async fetchData() {
        if (this._isCacheValid()) { // Check if we have valid cached data
            return this._getCachedResponse();
        }
        if (this.isFetching) { // Prevent duplicate fetches
            return this._waitForFetch();
        }
        this.isFetching = true;
        let lastError = null;
        // ! Retry logic
        for (let attempt = 1; attempt <= DataManager.CONFIG.RETRY_ATTEMPTS; attempt++) {
            try {
                const response = await fetch(DataManager.CONFIG.API_ENDPOINT);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                const data = await response.json();
                this._validateResponse(data); // ! Validate response structure
                this._storeData(data); // ! Store raw data
                this._buildLookupMaps(); // ! Build lookup maps
                this.lastFetchTime = Date.now(); // ! Mark as successfully fetched
                this.isFetching = false; // ! Mark fetch as complete
                return data;
            } catch (error) {
                lastError = error;
                // Wait before retrying (except on last attempt)
                if (attempt < DataManager.CONFIG.RETRY_ATTEMPTS) {
                    await this._sleep(DataManager.CONFIG.RETRY_DELAY * attempt);
                }
            }
        }
        this.isFetching = false; // ! All retries failed
        throw new Error(`Failed to fetch data after ${DataManager.CONFIG.RETRY_ATTEMPTS} attempts: ${lastError.message}`);
    }
    /**
     * ! Process CMS data into GeoJSON format
     * Filters out invalid items and builds feature collection
     */
    processGeoData() {
        this.allLocations = [];
        let skippedCount = 0;
        this.cmsData.forEach((item, index) => {
            const fields = item.fieldData;
            const projectName = fields.name; // ! Extract core fields
            const locationID = fields.slug; //! use slug as unique ID
            const locationLat = fields.latitude;
            const locationLong = fields.longitude;
            const typologyCodeId = fields['typology-code'];
            const statusCodeId = fields['status-code'];
            const showProject = fields['show-project'];
            const photoThumbnail = fields['photo-thumbnail'];
            const isFeatured = fields['featured'] || false;
            // ! Validation: SKIP items without coordinates
            if (!locationLat || !locationLong) {
                skippedCount++;
                return;
            }
            // ! Validation: SKIP items marked as hidden
            if (showProject === false) {
                skippedCount++;
                return;
            }
            const geoFeature = {
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [parseFloat(locationLong), parseFloat(locationLat)]
                },
                properties: {
                    id: locationID, //MARKER IDS AS SLUG
                    projectName: projectName,
                    arrayID: index,
                    typologyId: typologyCodeId,
                    typologyName: this.getTypologyName(typologyCodeId),
                    statusId: statusCodeId,
                    statusName: this.getStatusName(statusCodeId),
                    photoThumbnailUrl: photoThumbnail?.url || null,
                    isFeatured: isFeatured,
                }
            };
            this.allLocations.push(geoFeature);
        });
        this.visibleLocations.features = [...this.allLocations]; // Update visible locations (initially same as all)
        console.log(`data-m: ✅ Processed ${this.allLocations.length} valid locations (${skippedCount} skipped)`);
    }
    // ! TYPOLOGY LOOKUPS
    /**
     * ! GET TYPOLOGY [COLOR] BY [ID]
     * @param {string} typologyId - Webflow typology item ID
     * @returns {string} Hex color code
     */
    getTypologyColor(typologyId) {
        return this.typologyColorMap.get(typologyId) || '#000000'; // Default: black
    }
    /**
     * ! GET TYPOLOGY [NAME] BY [ID]
     * @param {string} typologyId - Webflow typology item ID
     * @returns {string} Typology name
     */
    getTypologyName(typologyId) {
        return this.typologyNameMap.get(typologyId) || 'Unknown';
    }
    /**
     * Get typology ID from slug
     * @param {string} slug - Typology slug from Webflow
     * @returns {string|null} Typology ID or null if not found
     */
    getTypologyIdFromSlug(slug) {
        return this.typologySlugToIdMap.get(slug) || null;
    }
    // ! STATUS LOOKUPS
    /**
     * ! GET STATUS [NAME] BY [ID]
     * @param {string} statusId - Webflow status item ID
     * @returns {string} Status name
     */
    getStatusName(statusId) {
        return this.statusNameMap.get(statusId) || 'Unknown';
    }
    /**
     * Get status ID from slug
     * @param {string} slug - Status slug from Webflow
     * @returns {string|null} Status ID or null if not found
     */
    getStatusIdFromSlug(slug) {
        return this.statusSlugToIdMap.get(slug) || null;
    }
    // ! FILTERING
    /**
     * Filter locations by criteria
     * @param {Object} filters - Filter criteria
     * @returns {Array} Filtered GeoJSON features
     */
    filterLocations(filters = {}) {
        const { typologyIds = [], statusIds = [] } = filters;
        let filtered = [...this.allLocations];
        // ! Filter by typology
        if (typologyIds.length > 0) {
            filtered = filtered.filter(feature =>
                typologyIds.includes(feature.properties.typologyId)
            );
        }
        // ! Filter by status
        if (statusIds.length > 0) {
            filtered = filtered.filter(feature =>
                statusIds.includes(feature.properties.statusId)
            );
        }
        this.visibleLocations.features = filtered;
        return filtered;
    }
    // ==================== PRIVATE METHODS ====================
    /**
     * Validate API response structure
     * @private
     */
    _validateResponse(data) {
        if (!data.success) {
            throw new Error('API response indicates failure');
        }
        if (!data.mapboxToken) {
            throw new Error('Missing Mapbox token in response');
        }
        if (!Array.isArray(data.items)) {
            throw new Error('Missing or invalid items array in response');
        }
        if (!Array.isArray(data.typologies)) {
            throw new Error('Missing or invalid typologies array in response');
        }
        if (!Array.isArray(data.statuses)) {
            throw new Error('Missing or invalid statuses array in response');
        }
    }
    /**
     * ! Store raw API data
     * @private
     */
    _storeData(data) {
        this.cmsData = data.items;
        this.typologies = data.typologies;
        this.statuses = data.statuses;
        this.mapboxToken = data.mapboxToken;
        this.projectTypologyCode = data.projectTypologyCode;
        this.projectStatusCode = data.projectStatusCode;
    }
    /**
     * ! Build fast lookup maps for typologies and statuses
     * @private
     */
    _buildLookupMaps() {
        // ! Clear existing maps
        this.typologyColorMap.clear();
        this.typologyNameMap.clear();
        this.typologySlugToIdMap.clear();
        this.statusNameMap.clear();
        this.statusSlugToIdMap.clear();
        // ! Build typology maps
        this.typologies.forEach(typology => {
            const id = typology.id;
            const name = typology.fieldData.name;
            const color = typology.fieldData.color;
            const slug = typology.fieldData.slug;
            this.typologyColorMap.set(id, color);
            this.typologyNameMap.set(id, name);
            // ! Map slug to ID for DOM interaction
            if (slug) {
                this.typologySlugToIdMap.set(slug, id);
            }
        });
        // ! Build status maps
        this.statuses.forEach(status => {
            const id = status.id;
            const name = status.fieldData.name;
            const slug = status.fieldData.slug;
            this.statusNameMap.set(id, name);
            // ! Map slug to ID for DOM interaction
            if (slug) {
                this.statusSlugToIdMap.set(slug, id);
            }
        });
    }
    /**
     * Check if cached data is still valid
     * @private
     */
    _isCacheValid() {
        if (!this.lastFetchTime || !this.cmsData) {
            return false;
        }
        const cacheAge = Date.now() - this.lastFetchTime;
        return cacheAge < DataManager.CONFIG.CACHE_DURATION;
    }
    /**
     * Get cached response object
     * @private
     */
    _getCachedResponse() {
        return {
            success: true,
            mapboxToken: this.mapboxToken,
            items: this.cmsData,
            typologies: this.typologies,
            count: this.cmsData.length,
            cached: true
        };
    }
    /**
     * Wait for ongoing fetch to complete
     * @private
     */
    async _waitForFetch() {
        while (this.isFetching) {
            await this._sleep(100);
        }
        return this._getCachedResponse();
    }
    /**
     * Sleep utility for retry delays
     * @private
     */
    _sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
//! EXPORT
export default DataManager;
// Legacy support for non-module environments (Webflow, older browsers)
if (typeof window !== 'undefined') {
    window.DataManager = DataManager;
}
